import { useEffect, useState } from 'react'
import { GitBranch, RefreshCw, ExternalLink } from 'lucide-react'
import { clsx } from 'clsx'
import { Link } from 'react-router-dom'
import { formatDistanceToNow } from 'date-fns'
import { api } from '../lib/api'

interface Release {
  repo: string
  tag: string
  name: string
  url: string
  published_at: string | null
  prerelease: boolean
  is_security: boolean
  cves: string[]
  summary: string
}

export default function GhReleasesPage() {
  const [data, setData] = useState<{ releases: Release[]; security_count: number; watched_repos: number; total: number } | null>(null)
  const [loading, setLoading] = useState(true)
  const [securityOnly, setSecurityOnly] = useState(false)

  const fetchAll = () => {
    setLoading(true)
    api.get('/api/devops/gh-releases', { params: { per_repo: 3 } })
      .then(r => setData(r.data))
      .finally(() => setLoading(false))
  }

  useEffect(() => { fetchAll() }, [])

  const list = (data?.releases || []).filter(r => !securityOnly || r.is_security)

  return (
    <div className="space-y-4 animate-fade-in">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h1 className="text-2xl font-bold text-text-primary flex items-center gap-3">
            <GitBranch size={24} className="text-cyan" /> GitHub Releases
          </h1>
          <p className="text-sm text-text-secondary mt-1">
            Watching {data?.watched_repos || 0} critical OSS repos · {data?.security_count || 0} security-tagged release{data?.security_count === 1 ? '' : 's'} in window
          </p>
        </div>
        <div className="flex items-center gap-2">
          <label className="flex items-center gap-1.5 text-xs text-text-secondary">
            <input type="checkbox" checked={securityOnly} onChange={e => setSecurityOnly(e.target.checked)} />
            Security only
          </label>
          <button type="button" onClick={fetchAll} className="btn-secondary text-xs">
            <RefreshCw size={12} className={loading ? 'animate-spin' : ''} /> Refresh
          </button>
        </div>
      </div>

      {loading && <div className="card p-6 text-center text-text-muted text-sm">Polling GitHub…</div>}

      {!loading && list.length === 0 && (
        <div className="card p-6 text-center text-text-muted text-sm">
          No releases. {securityOnly && 'Try clearing the "security only" filter.'}
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
        {list.map(r => (
          <a key={`${r.repo}/${r.tag}`} href={r.url} target="_blank" rel="noreferrer noopener"
            className={clsx('card p-3 hover:bg-surface-hover transition-colors flex flex-col gap-1.5',
              r.is_security && 'border-rose/40')}>
            <div className="flex items-center justify-between gap-2">
              <span className="font-mono text-blue-light text-[11px] truncate">{r.repo}</span>
              {r.is_security && <span className="badge-red text-[9px]">⚠ security</span>}
              {r.prerelease && <span className="badge text-[9px] bg-amber/15 text-amber-light border border-amber/40">pre</span>}
            </div>
            <div className="text-text-primary text-sm font-semibold">{r.tag}</div>
            {r.name && r.name !== r.tag && <div className="text-[11px] text-text-secondary line-clamp-1">{r.name}</div>}
            {r.cves.length > 0 && (
              <div className="flex flex-wrap gap-1">
                {r.cves.map(c => (
                  <Link key={c} to={`/app/cve/${encodeURIComponent(c)}`}
                    onClick={e => e.stopPropagation()}
                    className="font-mono text-[9px] px-1.5 py-0.5 rounded bg-rose/10 text-rose-light border border-rose/30">
                    {c}
                  </Link>
                ))}
              </div>
            )}
            {r.summary && <p className="text-[10px] text-text-muted line-clamp-2">{r.summary}</p>}
            <div className="flex items-center justify-between mt-auto text-[9px] text-text-muted">
              <span>{r.published_at && formatDistanceToNow(new Date(r.published_at), { addSuffix: true })}</span>
              <ExternalLink size={9} />
            </div>
          </a>
        ))}
      </div>
    </div>
  )
}
